import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

// UI
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';

// Icons
import { 
  Plus, Settings, Trash2, Edit, RefreshCw,
  FileText, Image as ImageIcon, Video, Package, Camera, 
  ArrowLeft, Save, X
} from 'lucide-react';

import { useAuth } from '../../contexts/AuthContext';
import API_BASE_URL from '../../services/api';
import ChunkedUploader from './components/ChunkedUploader';

const API = `${API_BASE_URL}/api`;

const mediaTypes = [
  { value: 'blog_post', label: 'Article de Blog', icon: FileText },
  { value: 'tract', label: 'Tract', icon: FileText },
  { value: 'gallery_image', label: 'Photo Galerie', icon: ImageIcon },
  { value: 'video', label: 'Vidéo', icon: Video },
  { value: 'documentary', label: 'Documentaire', icon: Camera },
  { value: 'shop_item', label: 'Article Boutique', icon: Package },
];

const statusOptions = [
  { value: 'draft', label: 'Brouillon' },
  { value: 'published', label: 'Publié' },
  { value: 'archived', label: 'Archivé' }
];

const MediaManagement = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Permissions
  useEffect(() => {
    if (!user || user.user_type !== 'fondateur') {
      navigate('/');
    }
  }, [user, navigate]);

  // Views & state
  const [view, setView] = useState('list'); // list | create | edit
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Filters
  const [filterType, setFilterType] = useState(searchParams.get('type') || '');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [query, setQuery] = useState('');

  // Form
  const [form, setForm] = useState({
    title: '',
    description: '',
    media_type: searchParams.get('type') || 'blog_post',
    status: 'draft',
    category: '',
    featured: false,
    // Shop fields
    price: '',
    currency: 'EUR',
    in_stock: true,
    stock_quantity: '',
    // Content
    content: '',
    excerpt: '',
    image_url: '',
    video_url: '',
    document_url: '',
    tags: []
  });
  const [editingId, setEditingId] = useState(null);
  const [newTag, setNewTag] = useState('');

  // Init from URL
  useEffect(() => {
    const typeFromUrl = searchParams.get('type');
    if (typeFromUrl) {
      setFilterType(typeFromUrl);
      setForm(prev => ({ ...prev, media_type: typeFromUrl }));
    }
    if (searchParams.get('action') === 'create') setView('create');
    loadCategories();
    loadItems();
    // eslint-disable-next-line
  }, []);

  const loadItems = async () => {
    try {
      setLoading(true); setError('');
      const params = new URLSearchParams();
      if (filterType) params.append('media_type', filterType);
      if (filterStatus) params.append('status', filterStatus);
      if (filterCategory) params.append('category', filterCategory);
      if (query) params.append('q', query);
      params.append('limit', '100');
      const res = await axios.get(`${API}/media/items/admin?${params.toString()}`, { headers: { Authorization: token ? `Bearer ${token}` : undefined } });
      setItems(res.data || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const res = await axios.get(`${API}/media/categories`, { headers: { Authorization: token ? `Bearer ${token}` : undefined } });
      setCategories((res.data || []).map(c => c.name));
    } catch (e) {
      console.error('Échec du chargement des catégories média:', e);
    }
  };

  const iconForType = (type) => (mediaTypes.find(t => t.value === type)?.icon || FileText);

  // Upload helper
  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await axios.post(`${API}/media/upload`, fd, { headers: { Authorization: token ? `Bearer ${token}` : undefined } });
    return res.data; // { url }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError(''); setSuccess('');
      const payload = {
        ...form,
        price: form.price !== '' ? parseFloat(form.price) : null,
        stock_quantity: form.stock_quantity !== '' ? parseInt(form.stock_quantity, 10) : null,
        tags: (form.tags || []).filter(t => t.trim() !== '')
      };
      if (editingId) {
        await axios.put(`${API}/media/items/${editingId}`, payload, { headers: { Authorization: token ? `Bearer ${token}` : undefined } });
        setSuccess("Élément mis à jour");
      } else {
        await axios.post(`${API}/media/items`, payload, { headers: { Authorization: token ? `Bearer ${token}` : undefined } });
        setSuccess("Élément créé");
      }
      await loadItems();
      setView('list');
      // reset form
      setForm(prev => ({ ...prev, title: '', description: '', content: '', excerpt: '', image_url: '', video_url: '', document_url: '' }));
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      title: item.title || '', description: item.description || '', media_type: item.media_type,
      status: item.status, category: item.category || '', featured: !!item.featured,
      price: item.price != null ? String(item.price) : '', currency: item.currency || 'EUR',
      in_stock: item.in_stock !== false, stock_quantity: item.stock_quantity != null ? String(item.stock_quantity) : '',
      content: item.content || '', excerpt: item.excerpt || '', image_url: item.image_url || '',
      video_url: item.video_url || '', document_url: item.document_url || '', tags: item.tags || []
    });
    setView('edit');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet élément ?')) return;
    try {
      await axios.delete(`${API}/media/items/${id}`, { headers: { Authorization: token ? `Bearer ${token}` : undefined } });
      setSuccess('Élément supprimé');
      await loadItems();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const toggleItemSale = async (item) => {
    try {
      const nextInStock = !(item.in_stock === true);
      const nextStatus = nextInStock ? 'published' : 'draft';
      await axios.put(`${API}/media/items/${item.id}`, { in_stock: nextInStock, status: nextStatus }, { headers: { Authorization: token ? `Bearer ${token}` : undefined } });
      await loadItems();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  // Rendering (Create/Edit)
  if (view === 'create' || view === 'edit') {
    const TypeIcon = iconForType(form.media_type);
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background/90 to-primary/5">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-4 mb-8">
              <Button variant="outline" onClick={() => { setView('list'); }}>
                <ArrowLeft className="h-4 w-4 mr-2" /> Retour
              </Button>
              <div>
                <h1 className="text-3xl font-bold">{view === 'create' ? 'Créer un contenu' : 'Modifier le contenu'}</h1>
                <p className="text-muted-foreground">Blog/Tracts/Galerie/Boutique/Vidéo/Documentaire</p>
              </div>
            </div>

            {error && <Alert className="mb-4" variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
            {success && <Alert className="mb-4"><AlertDescription>{success}</AlertDescription></Alert>}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2"><TypeIcon className="h-5 w-5" /> Métadonnées</CardTitle>
                    <CardDescription>Informations principales</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <label className="block text-sm mb-1">Type</label>
                      <Select value={form.media_type} onValueChange={(v) => setForm(prev => ({ ...prev, media_type: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {['blog_post','tract','gallery_image','video','documentary','shop_item'].map(v => (
                            <SelectItem key={v} value={v}>{v}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm mb-1">Titre</label>
                      <Input value={form.title} onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))} required />
                    </div>
                    <div>
                      <label className="block text-sm mb-1">Description</label>
                      <Textarea value={form.description} onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm mb-1">Statut</label>
                        <Select value={form.status} onValueChange={v => setForm(prev => ({ ...prev, status: v }))}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {statusOptions.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-sm mb-1">Catégorie</label>
                        <Input value={form.category} onChange={e => setForm(prev => ({ ...prev, category: e.target.value }))} placeholder="ex: T-shirts, Accessoires" />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <input id="featured" type="checkbox" checked={!!form.featured} onChange={e => setForm(prev => ({ ...prev, featured: e.target.checked }))} />
                      <label htmlFor="featured" className="text-sm">Mettre en avant</label>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Contenus</CardTitle>
                    <CardDescription>Texte principal et extrait</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <label className="block text-sm mb-1">Extrait (court)</label>
                      <Input value={form.excerpt} onChange={e => setForm(prev => ({ ...prev, excerpt: e.target.value }))} />
                    </div>
                    <div>
                      <label className="block text-sm mb-1">Contenu</label>
                      <Textarea value={form.content} onChange={e => setForm(prev => ({ ...prev, content: e.target.value }))} className="min-h-[160px]" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Image</CardTitle>
                    <CardDescription>URL directe ou upload</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Input placeholder="https://.../image.jpg" value={form.image_url} onChange={e => setForm(prev => ({ ...prev, image_url: e.target.value }))} />
                    <input type="file" accept="image/*" onChange={async (e) => {
                      const f = e.target.files?.[0]; if (!f) return; try { const up = await uploadFile(f); if (up?.url) setForm(prev => ({ ...prev, image_url: up.url })); } catch (err) { alert('Erreur upload image: ' + (err?.response?.data?.detail || err.message)); } finally { e.target.value=''; }
                    }} />
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle>Vidéo</CardTitle>
                    <CardDescription>URL directe (mp4/YouTube/Vimeo) ou upload (mp4)</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Input placeholder="https://.../video.mp4" value={form.video_url} onChange={e => setForm(prev => ({ ...prev, video_url: e.target.value }))} />
                    <input type="file" accept="video/mp4,video/*" onChange={async (e) => {
                      const f = e.target.files?.[0]; if (!f) return; try { const up = await uploadFile(f); if (up?.url) setForm(prev => ({ ...prev, video_url: up.url })); } catch (err) { alert('Erreur upload vidéo: ' + (err?.response?.data?.detail || err.message)); } finally { e.target.value=''; }
                    }} />
                  </CardContent>
                </Card>
              </div>

              <div className="flex justify-end">
                <Button type="submit">Sauvegarder</Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // LIST VIEW
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background/90 to-primary/5">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold">Media library</h1>
          <div className="flex gap-2">
            <Button onClick={() => setView('create')} className="flex items-center gap-2"><Plus className="h-4 w-4" />Créer</Button>
            <Button variant="outline" onClick={loadItems} className="flex items-center gap-2"><RefreshCw className="h-4 w-4" />Rafraîchir</Button>
          </div>
        </div>

        {error && <Alert className="mb-4" variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
        {success && <Alert className="mb-4"><AlertDescription>{success}</AlertDescription></Alert>}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map(item => (
            <Card key={item.id} className="overflow-hidden">
              <div className="aspect-video bg-muted">
                {item.image_url ? (
                  <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
                ) : item.video_url ? (
                  <video src={item.video_url} controls className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">Aperçu</div>
                )}
              </div>
              <CardContent className="p-4">
                <div className="font-semibold mb-1">{item.title}</div>
                <div className="text-xs text-muted-foreground mb-2">{item.media_type} • {item.status}</div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => handleEdit(item)}><Edit className="h-4 w-4 mr-1"/>Éditer</Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(item.id)}><Trash2 className="h-4 w-4 mr-1"/>Supprimer</Button>
                  {item.media_type === 'shop_item' && (
                    <Button size="sm" variant={item.in_stock ? 'destructive' : 'secondary'} onClick={() => toggleItemSale(item)}>
                      {item.in_stock ? 'Retirer de la vente' : 'Mettre en vente'}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MediaManagement;